import { flatten } from 'utils/flatten';
import messagesInEnglish from 'lang/en.json';
import messagesInEnglishGB from 'lang/en-GB.json';
import messagesInEnglishCA from 'lang/en-CA.json';
import messagesInEnglishIE from 'lang/en-IE.json';
import messagesInEnglishAU from 'lang/en-AU.json';
import messagesInCanadianFrench from 'lang/fr-CA.json';
import messagesInDutch from 'lang/nl-NL.json';
import messagesInFrench from 'lang/fr-FR.json';
import messagesInGerman from 'lang/de-DE.json';
import messagesInSpanish from 'lang/es-ES.json';
import messagesInItalian from 'lang/it-IT.json';
import messagesInNorwegian from 'lang/nb-NO.json';
import messagesInPolish from 'lang/pl.json';
import messagesInPortuguese from 'lang/pt-PT.json';
import messagesInHungarian from 'lang/hu-HU.json';
import messagesInSlovak from 'lang/sk-SK.json';
import { useMemo } from 'react';

export const DEFAULT_LOCALE = 'en';

// Locale keys are lowercase-hyphenated; getAvailableLocale normalizes user input
// (lowercases + converts underscores to hyphens) before lookup, so a canonical tag
// (e.g. `fr-FR`) resolves — see VIS-1695. Bare-language aliases (de, es, fr, it, nb/no,
// nl, pt, hu, sk) map a region-less locale onto a catalogue.
const messagesMap = new Map<string, Record<string, unknown>>([
  ['en', messagesInEnglish.serviceRequests],
  ['es-es', messagesInSpanish.serviceRequests],
  ['es', messagesInSpanish.serviceRequests],
  ['fr-fr', messagesInFrench.serviceRequests],
  ['fr', messagesInFrench.serviceRequests],
  ['fr-ca', messagesInCanadianFrench.serviceRequests],
  ['de-de', messagesInGerman.serviceRequests],
  ['de', messagesInGerman.serviceRequests],
  ['nl-nl', messagesInDutch.serviceRequests],
  ['nl', messagesInDutch.serviceRequests],
  ['it-it', messagesInItalian.serviceRequests],
  ['it', messagesInItalian.serviceRequests],
  ['nb-no', messagesInNorwegian.serviceRequests],
  ['nb', messagesInNorwegian.serviceRequests],
  ['no', messagesInNorwegian.serviceRequests],
  ['pl', messagesInPolish.serviceRequests],
  ['pt-pt', messagesInPortuguese.serviceRequests],
  ['pt', messagesInPortuguese.serviceRequests],
  ['hu-hu', messagesInHungarian.serviceRequests],
  ['hu', messagesInHungarian.serviceRequests],
  ['sk-sk', messagesInSlovak.serviceRequests],
  ['sk', messagesInSlovak.serviceRequests],
]);

/**
 * Regional ENGLISH variants, layered OVER `en` rather than replacing it.
 *
 * These are overlays, not catalogues: each file carries ONLY the strings its region actually says
 * differently — `Lift/Escalator`, `Construction Skip`, `Cancelled`. Everything else falls through to
 * `en`.
 *
 * Sparse on purpose. A full 203-key copy of `en` would work today and rot silently: reword a string in
 * `en` and the four copies keep the old wording forever, with nothing to catch it. Key-parity CI cannot
 * see it — the keys all still exist — and the stale value DIFFERS from `en`, so it reads as a deliberate
 * regional choice rather than a leftover. An overlay cannot rot that way, because the strings it does
 * not mention do not exist in it. Matches hqos (VIS-1643 / #6878), which layers its regional English
 * the same way.
 */
const regionalOverlays = new Map<string, Record<string, unknown>>([
  ['en-gb', messagesInEnglishGB.serviceRequests],
  ['en-ca', messagesInEnglishCA.serviceRequests],
  ['en-ie', messagesInEnglishIE.serviceRequests],
  ['en-au', messagesInEnglishAU.serviceRequests],
]);

// A structurally invalid tag reaches Intl only via the bare-language fallback: an input like
// `de_` or `de__AT` normalizes to `de-` / `de--at`, which still matches the `de` alias but is not
// a usable BCP-47 tag, so `Intl.NumberFormat(locale)` would throw a RangeError at render time.
// Keep the full normalized tag only when Intl can consume it; otherwise fall back to the bare
// language, which always can.
const isIntlSafe = (tag: string): boolean => {
  try {
    return Boolean(new Intl.NumberFormat(tag));
  } catch {
    return false;
  }
};

export const getAvailableLocale = (userLocale: string): [string, string] => {
  const normalized = userLocale.toLowerCase().replace(/_/g, '-');

  if (messagesMap.has(normalized) || regionalOverlays.has(normalized)) {
    return [normalized, normalized];
  }

  const [lang] = normalized.split('-');

  if (messagesMap.has(lang)) {
    return [lang, isIntlSafe(normalized) ? normalized : lang];
  }

  return [DEFAULT_LOCALE, DEFAULT_LOCALE];
};

interface LocaleData {
  messages: Record<string, string>;
  locale: string;
}

export const useLocaleData = (userLocale: string): LocaleData => {
  return useMemo(() => {
    const [messagesKey, locale] = getAvailableLocale(userLocale);
    const overlay = regionalOverlays.get(messagesKey);

    // An overlay is merged over `en`; a catalogue stands alone. Spreading `en` under every locale would
    // silently paper a missing translation over with English instead of surfacing it.
    const messages = overlay
      ? { ...flatten(messagesMap.get(DEFAULT_LOCALE)), ...flatten(overlay) }
      : flatten(messagesMap.get(messagesKey));

    return { locale, messages: messages as Record<string, string> };
  }, [userLocale]);
};
